import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useLang } from "../context/LangContext";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { AmountInput } from "../components/ui/amount-input";
import { formatINR, todayISO } from "../lib/format";
import { toast } from "sonner";

function emi(principal, ratePctMonthly, n) {
    const r = (ratePctMonthly || 0) / 100;
    if (!n) return 0;
    if (r === 0) return principal / n;
    return (principal * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
}

export default function LoanForm() {
    const { t } = useLang();
    const nav = useNavigate();
    const [params] = useSearchParams();
    const { id: editId } = useParams();
    const isEdit = !!editId;
    const preselect = params.get("customer") || "";

    const [customers, setCustomers] = useState([]);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({
        customer_id: preselect,
        loan_type: "personal",
        principal: 10000,
        interest_rate: 2,
        tenure_months: 12,
        start_date: todayISO(),
        payment_mode: "interest_principal",
        repayment_frequency: "monthly",
        notes: "",
    });

    useEffect(() => {
        api.get("/customers").then((r) => setCustomers(r.data));
        if (!isEdit) return;
        (async () => {
            try {
                const { data } = await api.get(`/loans/${editId}`);
                setForm({
                    customer_id: data.customer_id,
                    loan_type: data.loan_type,
                    principal: data.principal,
                    interest_rate: data.interest_rate,
                    tenure_months: data.tenure_months,
                    start_date: data.start_date,
                    payment_mode: data.payment_mode || "interest_principal",
                    repayment_frequency: data.repayment_frequency || "monthly",
                    notes: data.notes || "",
                });
            } catch (e) {
                toast.error(formatApiError(e));
            }
        })();
    }, [editId, isEdit]);

    function set(k, v) {
        setForm((s) => ({ ...s, [k]: v }));
    }

    const calcEmi = useMemo(
        () => emi(Number(form.principal), Number(form.interest_rate), Number(form.tenure_months)),
        [form.principal, form.interest_rate, form.tenure_months]
    );

    async function submit(e) {
        e.preventDefault();
        if (!form.customer_id) return toast.error("Select a customer");
        setBusy(true);
        try {
            if (isEdit) {
                await api.put(`/loans/${editId}`, form);
                toast.success("Loan updated");
                nav(`/loans/${editId}`);
            } else {
                const { data } = await api.post("/loans", form);
                toast.success("Loan created");
                nav(`/loans/${data.id}`);
            }
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    }

    return (
        <form onSubmit={submit} className="space-y-5">
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">
                {t("add_loan")}
            </h1>

            <Card className="rounded-2xl border-slate-100 p-5 sm:p-6 space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                        <Label>{t("customers")}</Label>
                        <Select
                            value={form.customer_id}
                            onValueChange={(v) => set("customer_id", v)}
                        >
                            <SelectTrigger className="h-11 rounded-xl" data-testid="loan-customer-select">
                                <SelectValue placeholder="Select customer" />
                            </SelectTrigger>
                            <SelectContent>
                                {customers.map((c) => (
                                    <SelectItem key={c.id} value={c.id}>
                                        {c.name} · {c.customer_code}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>{t("loan_type")}</Label>
                        <Select value={form.loan_type} onValueChange={(v) => set("loan_type", v)}>
                            <SelectTrigger className="h-11 rounded-xl" data-testid="loan-type-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="personal">{t("personal")}</SelectItem>
                                <SelectItem value="household">{t("household")}</SelectItem>
                                <SelectItem value="group">{t("group")}</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>{t("principal")}</Label>
                        <AmountInput
                            data-testid="loan-principal"
                            required
                            value={form.principal}
                            onChange={(v) => set("principal", v)}
                            className="h-11 rounded-xl"
                        />
                    </div>
                    <div>
                        <Label>{t("interest_rate")}</Label>
                        <AmountInput
                            data-testid="loan-interest"
                            required
                            value={form.interest_rate}
                            onChange={(v) => set("interest_rate", v)}
                            className="h-11 rounded-xl"
                        />
                    </div>
                    <div>
                        <Label>{t("tenure_months")}</Label>
                        <AmountInput
                            data-testid="loan-tenure"
                            required
                            value={form.tenure_months}
                            onChange={(v) => set("tenure_months", v)}
                            className="h-11 rounded-xl"
                        />
                    </div>
                    <div>
                        <Label>{t("start_date")}</Label>
                        <Input
                            data-testid="loan-start-date"
                            type="date"
                            required
                            value={form.start_date}
                            onChange={(e) => set("start_date", e.target.value)}
                            className="h-11 rounded-xl"
                        />
                    </div>
                    <div className="sm:col-span-2">
                        <Label>{t("payment_mode_loan")}</Label>
                        <Select
                            value={form.payment_mode}
                            onValueChange={(v) => set("payment_mode", v)}
                        >
                            <SelectTrigger className="h-11 rounded-xl" data-testid="loan-payment-mode-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="interest_only">{t("interest_only")}</SelectItem>
                                <SelectItem value="interest_principal">
                                    {t("interest_principal")}
                                </SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="sm:col-span-2">
                        <Label>Repayment frequency</Label>
                        <Select value={form.repayment_frequency} onValueChange={(v) => set("repayment_frequency", v)}>
                            <SelectTrigger className="h-11 rounded-xl" data-testid="loan-repayment-frequency-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="daily">Daily</SelectItem>
                                <SelectItem value="weekly">Weekly</SelectItem>
                                <SelectItem value="monthly">Monthly</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="sm:col-span-2">
                        <Label>{t("notes")}</Label>
                        <Textarea
                            value={form.notes}
                            onChange={(e) => set("notes", e.target.value)}
                            rows={2}
                            className="rounded-xl"
                            data-testid="loan-notes"
                        />
                    </div>
                </div>

                <Card className="rounded-xl bg-blue-50/60 border-blue-100 p-4">
                    <div className="text-[11px] uppercase tracking-wider font-semibold text-blue-900">
                        Estimated EMI ({t("interest_principal")})
                    </div>
                    <div className="num text-2xl font-bold text-blue-950 mt-1">
                        {formatINR(calcEmi)} / month
                    </div>
                    <div className="text-xs text-slate-600 mt-1">
                        First-month interest: {formatINR((form.principal * form.interest_rate) / 100)}
                    </div>
                </Card>
            </Card>

            <Button
                type="submit"
                disabled={busy}
                data-testid="save-loan-btn"
                className="h-12 rounded-xl bg-blue-900 hover:bg-blue-800 tap px-6 font-semibold"
            >
                {busy ? "Saving…" : isEdit ? "Update Loan" : t("save")}
            </Button>
        </form>
    );
}
